import { plannerApiPayload, plannerApiUrl } from './planner-api.ts'
import type { Venue } from './planning.ts'
import type { SearchDependencies } from './search.ts'

const operationTimeoutMs = 12_000

export const searchVenues: SearchDependencies['venues'] = async ({ centers, radius, type }, signal) => {
  const query = new URLSearchParams({
    ...(centers.length > 1 ? {
      centers: centers.map((area) => `${area.lat},${area.lon}`).join(';'),
    } : { lat: centers[0].lat.toString(), lon: centers[0].lon.toString() }),
    radius: radius.toString(), type,
  })
  const stop = new AbortController()
  const timer = setTimeout(() => stop.abort(new DOMException('Venue search timed out', 'TimeoutError')), operationTimeoutMs)
  const active = AbortSignal.any([signal, stop.signal])
  try {
    active.throwIfAborted()
    // Issued work is not aborted: even a late response must detect incompatibility.
    const { response, payload } = await whileActive(fetch(plannerApiUrl(`/api/venues?${query}`))
      .then(async (response) => ({ response,
        payload: await plannerApiPayload<{ venues?: Venue[]; error?: string }>(response),
      })), active)
    if (!response.ok) throw new Error(payload.error ?? 'Venue search failed')
    if (!Array.isArray(payload.venues)) throw new Error('Venue search returned an invalid result.')
    return payload.venues
  } finally {
    clearTimeout(timer)
  }
}

function whileActive<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const aborted = () => reject(signal.reason)
    if (signal.aborted) aborted()
    else signal.addEventListener('abort', aborted, { once: true })
    work.then((value) => {
      signal.removeEventListener('abort', aborted)
      if (signal.aborted) aborted()
      else resolve(value)
    }, (cause) => {
      signal.removeEventListener('abort', aborted)
      reject(cause)
    })
  })
}
